import { useState } from "react"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Icon from "@/v2-ui/icon"
import Skeleton from "@/v2-ui/skeleton"
import Badge from "@/v2-backoffice-client/components/badge"
import { useFeatureSurfaceFeaturesQuery } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.surfaceFeatures.query"

export type LicenseFeatureGroupInsideListProps = {
  surfaceCode: string
  containedFeatureCount: number
}

const LOADING_ROW_IDS = ["a", "b", "c", "d", "e"] as const

type LicenseFeatureGroupInsideRowsProps = LicenseFeatureGroupInsideListProps

/** The list itself; mounted only once the disclosure is opened, so the read happens then. */
function LicenseFeatureGroupInsideRows(
  props: LicenseFeatureGroupInsideRowsProps,
) {
  const { surfaceCode, containedFeatureCount } = props
  const { data, loading, error } = useFeatureSurfaceFeaturesQuery({
    variables: { surfaceCode },
  })
  const features = data?.getFeatureSurfaceFeatures ?? []

  return (
    <>
      <ErrorMessage error={error} />
      <ul className="max-h-56 divide-y overflow-y-auto pr-1">
        {loading
          ? LOADING_ROW_IDS.slice(
              0,
              Math.min(containedFeatureCount, LOADING_ROW_IDS.length),
            ).map((id) => (
              <li key={id} className="flex items-center gap-2 py-1">
                <Skeleton className="h-3 w-32" />
                <Skeleton className="h-2.5 w-20" />
              </li>
            ))
          : features.map((feature) => (
              <li key={feature.Code} className="flex items-center gap-2 py-1">
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-xs text-foreground">
                    {feature.Name}
                  </span>
                  <span className="block truncate font-mono text-[10px]">
                    {feature.Code}
                  </span>
                </span>
                {feature.IsSurface && (
                  <Badge size="xs" variant="warning">
                    Capability
                  </Badge>
                )}
              </li>
            ))}
      </ul>
    </>
  )
}

// What a capability is made of, inside its hover card: the count of
// registry features its component tree uses — labels, columns, modules —
// as one line, with the list itself behind a disclosure. The count is what
// makes the row read as a collection; the names are nice to have, so they
// load only when asked for, not on every hover (the card's content remounts
// on each open, so the disclosure starts closed every time).
function LicenseFeatureGroupInsideList(
  props: LicenseFeatureGroupInsideListProps,
) {
  const { surfaceCode, containedFeatureCount } = props
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className="flex min-h-0 flex-col gap-1 border-t pt-2">
      <button
        type="button"
        className="flex items-center gap-1.5 self-start rounded text-[11px] text-muted-foreground hover:text-foreground"
        aria-expanded={isOpen}
        data-feature-code="UI_BackofficeFeatureGroup_Capabilities_InsideToggle"
        onClick={() => {
          setIsOpen(!isOpen)
        }}
      >
        <Icon src="layers" size={10} />
        Inside · {containedFeatureCount}
        <Icon src={isOpen ? "caret-down" : "caret-right"} size={10} />
      </button>
      {isOpen && (
        <LicenseFeatureGroupInsideRows
          surfaceCode={surfaceCode}
          containedFeatureCount={containedFeatureCount}
        />
      )}
    </div>
  )
}

export default LicenseFeatureGroupInsideList
